import { html } from '@shared/utils/rendering/html';
import Form from '@shared/components/forms';
import Button from '@shared/components/buttons';
import Slider from '@shared/components/slider';

import { useRef, useMemo, useCallback } from 'preact/hooks';
import { useQueryClient, useMutation } from '@tanstack/react-query';

import { useSubcomponentFormSchema } from '../../../../../../configs/forms/subcomponents/subcomponent-form.schema';
import { subcomponentFormOptions } from '../../../../../../configs/forms/subcomponents/subcomponent-form.config';

import { RatingsFromId } from '../../../../../../constants/chips/ratings.constants';
import { CacheKeyNames } from '../../../../../../constants/utility/cache-key-names';

import { usePost } from '@core/utils/hooks/use-post';
import { useCache } from '@core/utils/context/cache-context';
import { useFormConfig } from '@core/utils/forms';
import { onSuccessfulResponse, upsertOne } from '@core/utils/helpers/mutations/mutation-effects';
import { updateEvidence } from './helpers/cache-updates';

const UploadEvidenceForm = ({ rowData, edit = false, ...props }) => {
    const schema = useSubcomponentFormSchema();
    const config = useFormConfig(); // to be used soon.

    const getCache = useCache().getKey;
    const queryClient = useQueryClient();

    const formRef = useRef(undefined);

    const {
        post: capture,
        isSubmitting,
        progress,
    } = usePost('/evidence', { asFormData: true, trackProgress: true });

    // Early return if no required data
    if (!rowData?.legalEntityAssessmentId || !rowData?.subcomponentId) {
        return html`
            <div class="card-header"><h4>Upload Evidence</h4></div>
            <div class="card-body">
                <p>Unable to load form: Missing required data.</p>
            </div>
        `;
    }

    const formData = useMemo(() => {
        if (!rowData) return {};

        const ratingObject = RatingsFromId(rowData?.ratingId);
        return {
            comment: rowData?.comment || '',
            rating: ratingObject ? [ratingObject] : [],
            uploadEvidence: rowData?.evidence?.fileName || null,
        };
    }, [rowData]);

    const captureMutation = useMutation({
        mutationFn: async (payload) => {
            const response = await capture(payload);
            return response?.data ?? response;
        },
        onSuccess: (response) => {
            const key = getCache(CacheKeyNames.legalEntityAssessmentData).cacheKey;
            if (!key && !response) return;
            try {
                onSuccessfulResponse(queryClient, key, updateEvidence, response);
            } catch (error) {
                console.error('Error updating cache:', error);
            }
        },
        onSettled: () => {
            const key = getCache(CacheKeyNames.legalEntityAssessmentData).cacheKey;
            queryClient.invalidateQueries({ queryKey: key });
        },
    });

    const handleSubmit = useCallback(
        (data) => {
            captureMutation.mutate({
                ...data,
                legalEntityAssessmentId: rowData.legalEntityAssessmentId,
                subcomponentId: rowData.subcomponentId,
            });
        },
        [captureMutation, rowData.legalEntityAssessmentId, rowData.subcomponentId]
    );
    return html`
        <div class="card-header"><h4>Upload Evidence</h4></div>
        <div class="card-body">
            <${Form}
                ref=${formRef}
                schema=${schema}
                onSubmit=${handleSubmit}
                initialValues=${formData}
                options=${{
                    ...subcomponentFormOptions,
                    formProps: {
                        ...subcomponentFormOptions.formProps,
                        disabled: isSubmitting || props?.readonly,
                    },
                }}
            />
            ${
                isSubmitting &&
                html`
                    <${Slider}
                        type="progress-tracker"
                        value=${progress}
                        validation=${{ min: 0, max: 100 }}
                        config=${{ showAsPercent: true }}
                        label=${{ text: 'Uploading' }}
                    />
                `
            }
        </div>
        <div class="card-footer">
            <${Button}
                label=${{ text: 'Cancel' }}
                buttonType="secondary"
                disabled=${isSubmitting || props?.readonly}
                onClick=${() => formRef?.current?.reset()}
            />
            <${Button}
                label=${{ text: edit ? 'Update' : 'Submit' }}
                buttonType="primary"
                disabled=${isSubmitting || props?.readonly}
                onClick=${(event) => formRef?.current?.handleSubmit(event)}
            />
        </div>
    `;
};

export default UploadEvidenceForm;
